'use client';

import { logger } from '@tuic/logger';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import GetSlash from './get-slash';

const UPDATE_FREQUENCY_IN_MS = 3_000;
const UPDATE_FREQUENCY_SHIP_IN_MS = 15_000;

/**
 * Enum representing the different types of network access
 */
export enum NetworkAccessType {
  /** Device is not connected to any network */
  Offline,
  /** Device is connected to the internet but not to the ship's network */
  Cloud,
  /** Device is connected to the ship's network */
  Ship,
}

/**
 * Hook for detecting the current network access type.
 * Periodically checks the network status and updates when it changes.
 * Also listens for online/offline events from the browser.
 */
export const useNetworkAccessType = (args: {
  useDebugEndpoints?: boolean;
}): NetworkAccessType | undefined => {
  const { useDebugEndpoints } = args;

  const [type, setType] = useState<NetworkAccessType>();
  const timeoutId = useRef<number>(undefined);

  // Create a GetSlash instance for network detection only
  // No credentials needed since we're only checking network status
  const getSlashClient = useMemo(() => {
    return new GetSlash({
      useDebugEndpoints,
    });
  }, [useDebugEndpoints]);

  const handleUpdateAndQueueNext = useCallback(async () => {
    window.clearTimeout(timeoutId.current);

    const type = await getNetworkAccessType(getSlashClient, useDebugEndpoints);

    setType(type);

    const frequency =
      type === NetworkAccessType.Ship
        ? UPDATE_FREQUENCY_SHIP_IN_MS
        : UPDATE_FREQUENCY_IN_MS;

    timeoutId.current = window.setTimeout(handleUpdateAndQueueNext, frequency);
  }, [getSlashClient, useDebugEndpoints]);

  useEffect(() => {
    handleUpdateAndQueueNext();

    const handleOffline = () => {
      setType(NetworkAccessType.Offline);
    };

    addEventListener('online', handleUpdateAndQueueNext);
    addEventListener('offline', handleOffline);

    return () => {
      window.clearTimeout(timeoutId.current);

      removeEventListener('online', handleUpdateAndQueueNext);
      removeEventListener('offline', handleOffline);
    };
  }, [handleUpdateAndQueueNext]);

  return type;
};

/**
 * Helper function to determine the current network access type.
 * Checks if the device is online, then tries to connect to the Get Slash API
 * to determine if the device is on the ship's network or just connected to the internet.
 */
const getNetworkAccessType = async (
  getSlashClient: GetSlash,
  useDebugEndpoints?: boolean,
): Promise<NetworkAccessType> => {
  if (!navigator.onLine) {
    return NetworkAccessType.Offline;
  }

  try {
    const response = await getSlashClient.status();

    // cloud: true means we're in cloud (not on ship)
    // cloud: false means we're definitely on ship
    if (response.cloud === false) {
      return NetworkAccessType.Ship;
    }

    if (response.cloud === true) {
      // In debug mode, simulate ship network
      if (useDebugEndpoints) {
        return NetworkAccessType.Ship;
      }
      return NetworkAccessType.Cloud;
    }
  } catch (e) {
    logger.error(e);
  }

  // Fallback to checking our own API endpoint
  try {
    const appResponse = await fetch('/api/ping', {
      method: 'GET',
    });

    const json = await appResponse.json();

    if ('pong' in json) {
      if (useDebugEndpoints) {
        return NetworkAccessType.Ship;
      }

      return NetworkAccessType.Cloud;
    }
  } catch (e) {
    logger.error(e);
  }

  return NetworkAccessType.Offline;
};
